import {memo, useEffect, useRef} from 'react'
import type {Virtualizer, VirtualItem} from '@tanstack/react-virtual'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useSelectedPathDigest, useSelectedPathDigestIsLoaded} from '../stores/selectors'

interface ScrollToSelectedDiffProps {
  virtualizer: Virtualizer<Window, Element>
  virtualItems: VirtualItem[]
  pathDigestToIndex: Map<string, number>
  diffEntryDigests: string[]
  loadManyPriority: (digests: string[]) => void
}

/**
 * Isolated component that handles scrolling to the selected diff entry.
 *
 * This is extracted from PullRequestVirtualizedDiffsList so that subscribing
 * to `selectedPathDigest` (which changes on every file tree click) does NOT
 * trigger a re-render of the entire virtualized diff list. Only this small
 * component re-renders when the selection changes, keeping the scroll-to
 * logic reactive without the cost of re-rendering all visible diff rows.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const ScrollToSelectedDiff = memo(function ScrollToSelectedDiff({
  virtualizer,
  virtualItems,
  pathDigestToIndex,
  diffEntryDigests,
  loadManyPriority,
}: ScrollToSelectedDiffProps) {
  const scrollIntentEnabled = isFeatureEnabled('pull_request_virtualization_scroll_intent')

  const selectedPathDigest = useSelectedPathDigest()

  // Subscribe reactively to the selected entry's isLoaded status so that
  // when the diff data finishes loading, this component re-renders and
  // the scroll effect can fire. Without this subscription, the component
  // would never know when an unloaded diff (outside the overscan window)
  // finishes loading.
  const selectedPathDigestIsLoaded = useSelectedPathDigestIsLoaded()

  // Cache the path we've already scrolled to so we do not keep snapping back while the user scrolls
  const hasScrolledToSelectionRef = useRef<string | null>(null)

  // Tracks whether the user has manually scrolled since the current selection was made.
  // When a selected file is outside the overscan window it loads asynchronously, and the
  // scroll-to happens only once it finishes loading. If the user scrolls away during that
  // window we must not yank them back to the selected file (#22844).
  const userScrolledSinceSelectionRef = useRef(false)

  useEffect(() => {
    // Reset on every selection change; we only care about user scrolls that happen *after*
    // a new file is selected.
    userScrolledSinceSelectionRef.current = false

    if (!scrollIntentEnabled) return
    if (!selectedPathDigest) return

    const markUserScrolled = () => {
      userScrolledSinceSelectionRef.current = true
    }

    // wheel/touchmove are produced only by genuine user gestures, never by the programmatic
    // virtualizer.scrollToIndex below, so listening here will not self-trigger.
    window.addEventListener('wheel', markUserScrolled, {passive: true})
    window.addEventListener('touchmove', markUserScrolled, {passive: true})

    return () => {
      window.removeEventListener('wheel', markUserScrolled)
      window.removeEventListener('touchmove', markUserScrolled)
    }
  }, [selectedPathDigest, scrollIntentEnabled])

  useEffect(() => {
    if (!selectedPathDigest) {
      hasScrolledToSelectionRef.current = null
      return
    }

    if (hasScrolledToSelectionRef.current === selectedPathDigest) {
      return
    }

    const targetIndex = pathDigestToIndex.get(selectedPathDigest)
    if (targetIndex === undefined) return

    if (!selectedPathDigestIsLoaded) {
      // load the diffs before the target to reduce diffs moving after they load
      loadManyPriority(
        [diffEntryDigests[targetIndex - 2], diffEntryDigests[targetIndex - 1], diffEntryDigests[targetIndex]].filter(
          digest => digest !== undefined,
        ),
      )
      return
    }

    // Check if the target diff is actually visible in the natural viewport range.
    // We can't just check virtualItems because the rangeExtractor forcibly adds selected items
    // to keep them in the DOM (for accessibility). Instead, check if the item's position
    // falls within the current scroll viewport.
    const virtualItem = virtualItems.find(item => item.index === targetIndex)
    const isActuallyInViewport =
      virtualItem &&
      virtualItem.start >= (virtualizer.scrollOffset ?? 0) - virtualItem.size &&
      virtualItem.start <= (virtualizer.scrollOffset ?? 0) + (virtualizer.scrollRect?.height ?? 0)

    if (isActuallyInViewport) {
      hasScrolledToSelectionRef.current = selectedPathDigest
      return
    }

    // If the user manually scrolled after this selection was made, respect their scroll
    // position instead of yanking them back to the selected file once it finishes loading.
    // Mark the selection as handled so we do not retry on subsequent renders. Gated by the
    // same flag that attaches the scroll listeners so behavior is consistent when it is off.
    if (scrollIntentEnabled && userScrolledSinceSelectionRef.current) {
      hasScrolledToSelectionRef.current = selectedPathDigest
      return
    }

    requestAnimationFrame(() => {
      virtualizer.scrollToIndex(targetIndex, {align: 'start'})
    })

    hasScrolledToSelectionRef.current = selectedPathDigest
  }, [
    selectedPathDigest,
    pathDigestToIndex,
    virtualizer,
    virtualItems,
    selectedPathDigestIsLoaded,
    loadManyPriority,
    diffEntryDigests,
    scrollIntentEnabled,
  ])

  return null
})
